// Content for Controlly's public SEO landing pages. Written for humans first,
// keywords used naturally. No fabricated stats, reviews, or competitor claims.

const CTA = { label: "Start free", to: "/login" };

export const SEO_PAGES = {
  "remote-desktop-software": {
    path: "/remote-desktop-software",
    title: "Controlly Remote Desktop Software | Secure Remote Access",
    description:
      "Controlly is remote desktop software that lets you securely view and control your computers from any browser. Free plan, one-time secure pairing, fair pricing.",
    h1: "Controlly Remote Desktop Software",
    lead:
      "Controlly is remote desktop software that lets you securely access and fully control your computers from any web browser — no clunky client to install on the device you're sitting at.",
    sections: [
      { h: "Remote access that just works", p: "Install a lightweight agent once on the computer you want to reach. It auto-starts, runs quietly, and uses zero bandwidth until you connect. From then on, open any browser and you're in control — mouse, keyboard, everything, in real time." },
      { h: "Secure by design", p: "Every session is encrypted, each machine pairs with a one-time token, and a clear on-screen indicator shows whenever a computer is being viewed. The person at the keyboard can cut the connection with one click, so there are never any silent takeovers." },
      { h: "Built for real people and small teams", p: "Controlly gives you professional remote access without the enterprise price tag or confusing per-seat licensing. Start on the free plan for a single computer, then upgrade only when you need more machines or teammates." },
    ],
    features: ["Full mouse & keyboard control", "Works in any modern browser", "Encrypted sessions & one-time pairing", "On-screen access indicator", "Windows, macOS & Linux agents"],
    related: ["browser-remote-desktop", "remote-support-software", "remote-desktop-for-small-business", "teamviewer-alternative"],
    cta: CTA,
    faq: [
      { q: "What is remote desktop software?", a: "Remote desktop software lets you see and control another computer's screen over the internet as if you were sitting in front of it. Controlly does this straight from your browser." },
      { q: "Do I need to install anything to control a machine?", a: "Only on the computer being controlled — a lightweight agent. The controlling side works in any modern browser with nothing to install." },
    ],
  },
  "free-remote-desktop": {
    path: "/free-remote-desktop",
    title: "Free Remote Desktop Software | Controlly Remote Desktop",
    description:
      "Get free remote desktop access with Controlly. Control one computer from any browser at no cost — full mouse and keyboard control, secure pairing, no credit card.",
    h1: "Free Remote Desktop Software",
    lead:
      "Controlly's free-forever plan gives you real remote desktop access to one computer from any browser — full control, secure sessions, and no credit card required.",
    sections: [
      { h: "What you get for free", p: "The free plan includes full remote control of one computer, encrypted sessions, one-time secure pairing, and the same fast browser-based experience as our paid plans. It's perfect for keeping an eye on a home PC or helping out a family member." },
      { h: "Upgrade only when you need to", p: "Need more computers or want to share access with family or teammates? Paid plans start at just $8/month. There are no commercial-use lockouts and no surprise fees — you upgrade only when you outgrow the free plan." },
    ],
    features: ["1 computer, free forever", "Full remote control from the browser", "No credit card required", "Secure encrypted sessions"],
    related: ["remote-desktop-software", "browser-remote-desktop", "teamviewer-alternative"],
    cta: { label: "Get started free", to: "/login" },
    faq: [
      { q: "Is Controlly really free?", a: "Yes. The free-forever plan lets you remotely control one computer from any browser with no credit card. Paid plans start at $8/month for more computers or users." },
    ],
  },
  "browser-remote-desktop": {
    path: "/browser-remote-desktop",
    title: "Browser Remote Desktop | Web-Based Remote Access — Controlly",
    description:
      "Controlly is a web-based remote desktop you run from any browser. No client to install on your side — just log in and control your computers securely from anywhere.",
    h1: "Remote Desktop From Any Browser",
    lead:
      "Controlly is a web-based remote desktop. There's nothing to install on the device you're sitting at — open Chrome, Edge, Safari or Firefox, sign in, and take control of your computers instantly.",
    sections: [
      { h: "No client on your side", p: "Traditional remote access tools make you install software on both ends. Controlly only needs a lightweight agent on the machine you want to reach. You control it from a normal browser tab — great when you're on a borrowed or locked-down computer." },
      { h: "Access from anywhere", p: "Any modern browser, any network. All your computers appear in one simple dashboard, ready to control in real time with full mouse and keyboard input." },
    ],
    features: ["Zero install on the controlling device", "Works on any OS with a browser", "Real-time mouse & keyboard", "One dashboard for every machine"],
    related: ["remote-desktop-software", "free-remote-desktop", "anydesk-alternative"],
    cta: CTA,
    faq: [
      { q: "Can I access my computer from any web browser?", a: "Yes. After installing the Controlly agent on a computer, you can control it from any modern browser — Chrome, Edge, Safari, or Firefox — with nothing to install on the device you're using." },
    ],
  },
  "remote-desktop-for-small-business": {
    path: "/remote-desktop-for-small-business",
    title: "Remote Desktop for Small Business | Controlly Remote Desktop",
    description:
      "Affordable remote desktop software for small businesses. Controlly gives your team secure browser-based remote access with flat, fair pricing — no per-seat surprises.",
    h1: "Remote Desktop for Small Business",
    lead:
      "Controlly is affordable remote desktop software built for small businesses — secure browser-based access to your office computers with simple, flat pricing instead of costly enterprise seats.",
    sections: [
      { h: "Fair, predictable pricing", p: "Enterprise remote-access tools can cost hundreds per seat each year. Controlly keeps it flat and simple: plans from $8/month, with the Team plan covering up to 30 computers and multiple users." },
      { h: "Share access the right way", p: "Invite teammates and give each person access only to the machines they need. Everyone works from the same clean dashboard, and every session is encrypted with an on-screen indicator so staff always know when a machine is being viewed." },
      { h: "Support your customers too", p: "With Controlly Snap you can jump onto a customer's computer for one-off help without them creating an account — ideal for shops and IT providers." },
    ],
    features: ["Up to 30 computers on Team", "Per-machine access control", "Multiple users & shared workspaces", "Flat monthly pricing from $8"],
    related: ["remote-it-support", "remote-support-software", "snap", "teamviewer-alternative"],
    cta: { label: "See pricing", to: "/#pricing" },
    faq: [
      { q: "How much does Controlly cost for a small business?", a: "Plans are flat and simple: Solo $8/mo, Group $15/mo, and Team $29/mo (up to 30 computers, 3 users). There are no per-seat enterprise fees." },
    ],
  },
  "remote-it-support": {
    path: "/remote-it-support",
    title: "Remote IT Support Software | Controlly Remote Desktop",
    description:
      "Controlly is remote IT support software for technicians and shops. Fix computers remotely from your browser, or help customers instantly with Controlly Snap.",
    h1: "Remote IT Support Software",
    lead:
      "Controlly helps IT technicians and support providers fix computers remotely from any browser — either through installed agents you manage, or instant one-time sessions with Controlly Snap.",
    sections: [
      { h: "Two ways to support", p: "For machines you look after regularly, install the Controlly agent once and connect any time. For quick, one-off help, use Controlly Snap: the person you're helping runs a temporary helper and you're on their screen in seconds — no account required on their end." },
      { h: "Professional and trustworthy", p: "Encrypted sessions and a persistent on-screen banner mean the person being helped always knows what's happening. When a Controlly Snap session ends, the temporary helper removes itself automatically." },
    ],
    features: ["Attended & unattended support", "Instant one-time help with Controlly Snap", "Encrypted sessions", "On-screen access indicator", "Self-removing temporary helper"],
    related: ["snap", "remote-support-software", "remote-desktop-for-small-business", "anydesk-alternative"],
    cta: CTA,
    faq: [
      { q: "Can I provide remote IT support without installing permanent software?", a: "Yes. Controlly Snap lets you help someone through a temporary, self-removing helper — no permanent install and no account needed for the person receiving support." },
    ],
  },
  "remote-support-software": {
    path: "/remote-support-software",
    title: "Remote Support Software | Controlly Remote Desktop",
    description:
      "Controlly is remote support software that lets you help anyone from your browser. Start a secure session, share a link, and take control — with or without an account.",
    h1: "Remote Support Software",
    lead:
      "Controlly is remote support software that makes helping people simple: connect to computers you manage, or start an instant one-time session and send a link to someone who needs a hand.",
    sections: [
      { h: "Help in seconds", p: "Start a session from your dashboard and share a secure link (or read a 6-digit code) with the person you're helping. They run a one-time helper and you're viewing and controlling their screen right away." },
      { h: "Safe for both sides", p: "Sessions are encrypted and a clear banner shows whenever the screen is being viewed. The person being helped can end the session at any time, and the temporary helper cleans itself up afterward." },
    ],
    features: ["Instant session links", "6-digit backup code", "Encrypted, consent-based sessions", "No account needed for the person you help"],
    related: ["snap", "remote-it-support", "remote-desktop-software"],
    cta: CTA,
    faq: [
      { q: "Does the person I'm helping need an account?", a: "No. With Controlly Snap they simply run a one-time helper from a link or code — no signup, and nothing is left installed afterward." },
    ],
  },
  "teamviewer-alternative": {
    path: "/teamviewer-alternative",
    title: "TeamViewer Alternative | Controlly Remote Desktop",
    description:
      "Looking for a TeamViewer alternative? Controlly is simple, browser-based remote desktop software with flat, affordable pricing and no per-seat enterprise licensing.",
    h1: "A Simpler TeamViewer Alternative",
    lead:
      "If you want straightforward remote desktop software without enterprise pricing or complex licensing, Controlly is a TeamViewer alternative built for individuals, families, and small businesses.",
    sections: [
      { h: "Why people look for an alternative", p: "Many remote-access tools are priced and packaged for large enterprises, with high per-seat costs and licensing that's hard to understand. Controlly takes a simpler approach: flat pricing from $8/month and a single lightweight agent that just works." },
      { h: "What Controlly focuses on", p: "Fast browser-based control, encrypted sessions, one-time secure pairing, and a clear on-screen indicator so people always know when a machine is being viewed. There's a genuine free-forever plan so you can try it with no commitment." },
    ],
    features: ["Flat pricing from $8/month", "Free-forever plan", "Browser-based — nothing to install to connect", "Instant one-time support with Controlly Snap"],
    related: ["anydesk-alternative", "remote-desktop-software", "remote-desktop-for-small-business"],
    cta: { label: "Compare plans", to: "/#pricing" },
    faq: [
      { q: "How is Controlly different from TeamViewer?", a: "Controlly is built for individuals, families, and small businesses rather than large enterprises, with flat pricing from $8/month, a free-forever plan, and a browser-based experience. Feature sets differ, so choose the tool that best fits your needs." },
    ],
  },
  "anydesk-alternative": {
    path: "/anydesk-alternative",
    title: "AnyDesk Alternative | Controlly Remote Desktop",
    description:
      "Considering an AnyDesk alternative? Controlly offers secure, browser-based remote desktop access with a free plan and simple, flat pricing for individuals and teams.",
    h1: "A Friendly AnyDesk Alternative",
    lead:
      "Controlly is an AnyDesk alternative that runs entirely in your browser, with a free-forever plan and simple flat pricing — designed for people and small teams, not just enterprises.",
    sections: [
      { h: "Control from the browser", p: "You don't need a desktop client on the device you're sitting at. Sign in from any modern browser and control your computers in real time, with a lightweight agent only on the machines you want to reach." },
      { h: "Clear pricing and consent", p: "Plans are flat and easy to understand, starting at $8/month with a free tier to get going. Every session is encrypted and shows an on-screen indicator, so the person at the machine is always in control." },
    ],
    features: ["Runs in any browser", "Free-forever plan", "Flat, simple pricing", "On-screen access indicator"],
    related: ["teamviewer-alternative", "browser-remote-desktop", "remote-desktop-software"],
    cta: { label: "Compare plans", to: "/#pricing" },
    faq: [
      { q: "How is Controlly different from AnyDesk?", a: "Controlly is browser-based with a free-forever plan and flat pricing aimed at individuals and small teams. Feature sets differ between tools, so pick the one that fits your workflow." },
    ],
  },
  "controlly-vs-teamviewer": {
    path: "/controlly-vs-teamviewer",
    title: "Controlly vs TeamViewer | Remote Desktop Comparison",
    description:
      "Controlly vs TeamViewer: compare browser-based remote desktop access, pricing approach, and who each tool is built for, to choose the right remote access software.",
    h1: "Controlly vs TeamViewer",
    lead:
      "A factual look at how Controlly and TeamViewer differ, so you can pick the remote desktop software that best fits your needs.",
    sections: [
      { h: "Who each is built for", p: "TeamViewer is a long-established remote-access product with a broad enterprise feature set. Controlly focuses on individuals, families, and small businesses that want straightforward, affordable browser-based remote access." },
      { h: "Pricing approach", p: "Controlly uses flat, published pricing starting at $8/month with a free-forever plan. Enterprise-oriented tools often use per-seat licensing that varies by use case. Always check each vendor's current pricing for your situation." },
      { h: "How you connect", p: "Controlly runs in the browser — nothing to install on the controlling device — and adds Controlly Snap for instant, account-free support sessions." },
    ],
    features: ["Browser-based control", "Free-forever plan", "Flat pricing from $8/month", "Controlly Snap for one-time support"],
    related: ["teamviewer-alternative", "controlly-vs-anydesk", "remote-desktop-software"],
    cta: { label: "See Controlly pricing", to: "/#pricing" },
  },
  "controlly-vs-anydesk": {
    path: "/controlly-vs-anydesk",
    title: "Controlly vs AnyDesk | Remote Desktop Comparison",
    description:
      "Controlly vs AnyDesk: compare browser-based remote access, pricing style, and intended users to choose the remote desktop software that fits you best.",
    h1: "Controlly vs AnyDesk",
    lead:
      "A straightforward comparison of Controlly and AnyDesk to help you choose the right remote desktop software.",
    sections: [
      { h: "Intended users", p: "AnyDesk is a widely used remote-access tool with options across personal and business tiers. Controlly is aimed squarely at individuals, families, and small businesses who want simple, affordable, browser-based access." },
      { h: "Connecting and pricing", p: "Controlly runs entirely in the browser with a free-forever plan and flat pricing from $8/month. Check each vendor's current pricing and feature list to confirm what suits your workflow." },
    ],
    features: ["Runs in the browser", "Free-forever plan", "Flat pricing from $8/month", "Instant one-time support with Controlly Snap"],
    related: ["anydesk-alternative", "controlly-vs-teamviewer", "browser-remote-desktop"],
    cta: { label: "See Controlly pricing", to: "/#pricing" },
  },
  "snap": {
    path: "/snap",
    title: "Instant Remote Support with Controlly Snap | No Account Needed",
    description:
      "Controlly Snap is instant, temporary remote support. Send a link or 6-digit code, the person runs a one-time helper, you take control — no account, nothing left behind.",
    h1: "Instant Remote Support with Controlly Snap",
    lead:
      "Controlly Snap lets you provide instant, temporary remote support without asking the person you're helping to create an account. Send a link or read them a 6-digit code and you're on their screen in seconds.",
    sections: [
      { h: "How Controlly Snap works", p: "From your dashboard, start a Snap session. You get a secure link and a 6-digit backup code to give the person you're helping. They run a small one-time helper on their computer, and it connects them straight to you — no signup, no permanent install." },
      { h: "Why technicians love it", p: "It removes the biggest friction in remote support: getting the other person set up. There's no account to create and nothing to uninstall — when the session ends, the temporary helper removes itself automatically." },
      { h: "Safe and consent-based", p: "Sessions are encrypted and a clear banner shows the whole time a screen is being viewed. The person being helped stays in control and can end the session whenever they like." },
    ],
    features: ["No account for the person you help", "Secure link + 6-digit backup code", "One-time, self-removing helper", "Works on Windows, macOS & Linux", "Encrypted, consent-based sessions"],
    related: ["remote-it-support", "remote-support-software", "remote-desktop-for-small-business"],
    cta: { label: "Start free", to: "/login" },
    faq: [
      { q: "Does the person receiving support need a Controlly account?", a: "No. With Controlly Snap they just run a one-time helper from your link or code. There's no signup, and the helper removes itself when the session ends." },
      { q: "Is Controlly Snap secure?", a: "Yes. Sessions are encrypted, an on-screen banner shows whenever the screen is being viewed, and the person being helped can end the session at any time." },
    ],
  },
};

export const SEO_SLUGS = Object.keys(SEO_PAGES);

// Short, human labels for footer/internal links. Order = footer display order.
export const SEO_FOOTER_LINKS = [
  { slug: "remote-desktop-software", label: "Remote Desktop Software" },
  { slug: "free-remote-desktop", label: "Free Remote Desktop" },
  { slug: "browser-remote-desktop", label: "Browser Remote Desktop" },
  { slug: "remote-desktop-for-small-business", label: "Remote Desktop for Small Business" },
  { slug: "remote-it-support", label: "Remote IT Support" },
  { slug: "remote-support-software", label: "Remote Support Software" },
  { slug: "snap", label: "Controlly Snap" },
  { slug: "teamviewer-alternative", label: "TeamViewer Alternative" },
  { slug: "anydesk-alternative", label: "AnyDesk Alternative" },
  { slug: "controlly-vs-teamviewer", label: "Controlly vs TeamViewer" },
  { slug: "controlly-vs-anydesk", label: "Controlly vs AnyDesk" },
];

// Build the per-page JSON-LD: SoftwareApplication + BreadcrumbList + optional FAQPage.
export function buildSeoJsonLd(page) {
  const SITE = "https://getcontrolly.com";
  const blocks = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Controlly",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS, Linux, Web",
      description: page.description,
      url: `${SITE}${page.path}`,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free-forever plan available; paid plans from $8/month.",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: page.h1, item: `${SITE}${page.path}` },
      ],
    },
  ];
  if (page.faq && page.faq.length) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  return blocks;
}
